import { type JSX } from 'react';
import { type LucideIcon } from 'lucide-react';
import {
  SidebarNavSection,
  type SidebarNavSectionItem,
} from './SidebarNavSection';

interface AiAppSecNavGroupProps {
  /** Icon for the parent row (ShieldPlus). */
  icon: LucideIcon;
  /** Route of the Secure AI Coding Enforcement page. */
  secureAiCodingTo: string;
  /** Route of the Threat Modeling page. */
  threatModelingTo: string;
  /** Route of the Vulnerability Discovery with LLM page. */
  vulnerabilityDiscoveryTo: string;
}

/**
 * AiAppSecNavGroup — the "AI AppSec" sidebar section (KIR-3925).
 *
 * Groups the AI application security pages under one title via
 * {@link SidebarNavSection}: "Secure AI Coding Enforcement" (existing page,
 * unchanged route), "Threat Modeling" (the KIR-3925 catalog), and
 * "Vulnerability Discovery with LLM" (the OpenAnt under-construction page).
 * Each child keeps its own top-level route, so the child paths are passed as
 * `activePaths` to keep the section marked active on all of them. The whole
 * section rides the `ai_instruction_catalog` flag — the caller renders it only
 * when that flag is on. The parent row navigates to the first child (there is
 * no hub screen).
 */
export function AiAppSecNavGroup({
  icon,
  secureAiCodingTo,
  threatModelingTo,
  vulnerabilityDiscoveryTo,
}: AiAppSecNavGroupProps): JSX.Element {
  const items: SidebarNavSectionItem[] = [
    { label: 'Secure AI Coding Enforcement', to: secureAiCodingTo },
    { label: 'Threat Modeling', to: threatModelingTo },
    {
      label: 'Vulnerability Discovery with LLM',
      to: vulnerabilityDiscoveryTo,
    },
  ];

  return (
    <SidebarNavSection
      label="AI AppSec"
      to={secureAiCodingTo}
      icon={icon}
      items={items}
      activePaths={items.map(item => item.to)}
    />
  );
}
